import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Box from "@mui/material/Box";
import { Tooltip, useMediaQuery, Drawer } from "@mui/material";
import config from "../config";
import subscriptionStatus from "../components/SubscriptionStatus";
import axios from "axios";
import { useLocation } from "react-router-dom";
// Consolidated React Icons imports
import { FaBuilding } from "react-icons/fa";
import { GiSpades } from "react-icons/gi";

import {
  MdEdit,
  MdLock,
  MdStorefront,
  MdExitToApp,
  MdPalette,
  MdFeedback,
  MdSupport,
  MdMap,
  MdCampaign,
  MdDashboard,
  MdProductionQuantityLimits,
  MdPeople,
  MdCategory,
  MdShoppingCart,
  MdAccountBalance,
  MdSettings,
  MdChevronLeft,
  MdChevronRight,
  MdHome,
  MdHelp,
  MdMailOutline,
  MdPages,
  MdBusinessCenter,
  MdAttachMoney,
  MdDelete,
  MdAddCircleOutline,
  MdVisibility,
  MdVisibilityOff,
  MdSubscriptions,
  MdListAlt,
  MdBackup,
  MdOutlineIntegrationInstructions, // Used for the Integrations parent menu
  MdOutlineArticle,
} from "react-icons/md";
import { TbActivity } from "react-icons/tb";

const baseUrl = config.baseURL;

// --- Enterprise Style Variables ---
// --- Enterprise Style Variables ---
const EnterpriseColors = {
  SidebarBg: "#1F2937", // Dark Slate/Almost Black
  PrimaryText: "#F9FAFB", // Light Gray/White
  SecondaryText: "#9CA3AF", // Medium Gray
  HoverBg: "#374151", // Slightly Lighter Dark Gray for hover
  ActiveBg: "#4B5563", // Darker Gray for active state
  Accent: "#4F46E5", // Indigo/Blue for primary actions
  IconDefault: "#D1D5DB", // Light Gray for icons
  IconHover: "#F9FAFB", // White for hovered icons
  // ✨ NEW: Light Gray for the Create New Module button
  NewModuleButtonBg: "#D1D5DB", // Light Gray
  NewModuleButtonText: "#1F2937", // Dark text for light button
  NewModuleButtonHover: "#E5E7EB", // Slightly lighter gray for hover

  MobileBarBg: "#1F2937", // Same as SidebarBg (Dark Slate/Almost Black)
  MobileIconActive: "#4F46E5", // Accent color (Indigo/Blue)
  MobileIconInactive: "#9CA3AF", // SecondaryText (Medium Gray)
};

// --- Helper Components ---

// Helper component for repeated submenu links
const SubMenuItem = ({
  name,
  path,
  icon: Icon,
  isCollapsed,
  hoveredIndex,
  setHoveredIndex,
  isPremiumOnly,
  tooltip,
}) => {
  const isHovered = hoveredIndex === name;
  const itemStyle = {
    width: "100%",
    borderRadius: "6px",
    backgroundColor: isHovered ? EnterpriseColors.HoverBg : "transparent",
    color: EnterpriseColors.PrimaryText,
    display: "flex",
    alignItems: "center",
    marginBottom: "4px",
    padding: "8px 12px",
    textDecoration: "none",
    transition: "background-color 0.15s ease",
    opacity: isPremiumOnly ? 0.6 : 1,
    pointerEvents: isPremiumOnly ? "none" : "auto",
  };

  const iconStyle = {
    marginRight: "10px",
    fontSize: "20px",
    color: isHovered
      ? EnterpriseColors.IconHover
      : EnterpriseColors.IconDefault,
  };

  const textStyle = {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    color: EnterpriseColors.PrimaryText,
    textAlign: "left",
  };

  const linkContent = (
    <Link
      to={path}
      className="block"
      style={itemStyle}
      onMouseEnter={() => setHoveredIndex(name)}
      onMouseLeave={() => setHoveredIndex(null)}
      onClick={(e) => {
        if (isPremiumOnly) e.preventDefault();
      }}
    >
      {React.createElement(Icon, { style: iconStyle })}
      {!isCollapsed && <span style={textStyle}>{name}</span>}
    </Link>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="right" arrow>
      <div style={{ paddingLeft: isCollapsed ? "0px" : "12px" }}>
        {linkContent}
      </div>
    </Tooltip>
  ) : (
    <div style={{ paddingLeft: isCollapsed ? "0px" : "12px" }}>
      {linkContent}
    </div>
  );
};

// New component for dynamic and static module items
// New component for dynamic and static module items
const ModuleMenuItem = ({
  module,
  isCollapsed,
  hoveredIndex,
  setHoveredIndex,
  onEdit,
  onDelete,
  onHide,
  onRestore,
  closeDrawer, // 💡 ADDED PROP: Used to close the drawer on mobile navigation
}) => {
  // 💡 FIX: Import and use useMediaQuery to define isMobile
  const isMobile = useMediaQuery("(max-width:768px)");
  const isHidden = module.isHidden;
  const name = module.name;

  // 💡 MODIFICATION: Set the dynamic path for all modules
  const path = `/dashboard/${module.slug}`;

  const isStatic = module.isStatic;
  const ModuleIcon = module.icon;
  const isHovered = hoveredIndex === name;

  const itemStyle = {
    width: "100%",
    borderRadius: "6px",
    backgroundColor: isHovered ? EnterpriseColors.HoverBg : "transparent",
    color: EnterpriseColors.PrimaryText,
    display: "flex",
    alignItems: "center",
    marginBottom: "4px",
    // Ensure padding adjusts for collapsed vs expanded
    padding: isCollapsed ? "12px 0" : "8px 12px",
    textDecoration: "none",
    transition: "background-color 0.15s ease",
    opacity: isHidden ? 0.5 : 1,
    justifyContent: isCollapsed ? "center" : "flex-start",
  };

  const iconStyle = {
    fontSize: isCollapsed ? "24px" : "20px",
    color: isHovered
      ? EnterpriseColors.IconHover
      : EnterpriseColors.IconDefault,
    marginRight: isCollapsed ? "0" : "10px",
  };

  const textStyle = {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    color: EnterpriseColors.PrimaryText,
    textAlign: "left",
    flexGrow: 1, // Allows text to take up available space
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const actionIconStyle = {
    cursor: "pointer",
    fontSize: "16px",
    color: isHovered
      ? EnterpriseColors.IconHover
      : EnterpriseColors.SecondaryText,
    marginLeft: "8px",
    transition: "color 0.15s ease",
  };

  const content = (
    <div
      style={itemStyle}
      onMouseEnter={() => setHoveredIndex(name)}
      onMouseLeave={() => setHoveredIndex(null)}
      // 💡 ADD onClick for the whole block to handle collapse/expand in desktop mode
      // NOTE: The navigation is handled by the wrapper <Link> below.
    >
      {React.cloneElement(ModuleIcon, { style: iconStyle })}

      {!isCollapsed && (
        <>
          <span // Use <span> for the name when wrapped by Link
            style={{
              ...textStyle,
              textDecoration: "none",
              color: EnterpriseColors.PrimaryText,
            }}
          >
            {name}
          </span>

          {/* Action icons are kept outside the primary text span */}
          {!isStatic && isHovered && (
            <>
              <Tooltip title="Edit Module" placement="top" arrow>
                <MdEdit
                  style={{ ...actionIconStyle, marginRight: "4px" }}
                  onClick={(e) => {
                    e.preventDefault(); // Prevent link navigation
                    e.stopPropagation(); // Prevent Link onClick from firing
                    onEdit(module.id);
                  }}
                />
              </Tooltip>
              <Tooltip title="Delete Module" placement="top" arrow>
                <MdDelete
                  style={{ ...actionIconStyle, color: "#EF4444" }}
                  onClick={(e) => {
                    e.preventDefault(); // Prevent link navigation
                    e.stopPropagation();
                    onDelete(module.id);
                  }}
                />
              </Tooltip>
            </>
          )}

          <Tooltip
            title={isHidden ? "Restore Module" : "Hide Module"}
            placement="top"
            arrow
          >
            {isHidden ? (
              <MdVisibility
                style={{ ...actionIconStyle, color: EnterpriseColors.Accent }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRestore(module.id);
                }}
              />
            ) : (
              <MdVisibilityOff
                style={actionIconStyle}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onHide(module.id);
                }}
              />
            )}
          </Tooltip>
        </>
      )}
    </div>
  );

  // 💡 MODIFICATION: Wrap the entire content in a single <Link> using the dynamic path
  return (
    <Link
      to={path}
      style={{ textDecoration: "none", display: "block" }}
      // On mobile, close the drawer when a link is clicked
      onClick={isMobile ? closeDrawer : undefined}
    >
      {isCollapsed ? (
        <Tooltip title={name} placement="right" arrow>
          {content}
        </Tooltip>
      ) : (
        <div style={{ paddingLeft: "0px" }}>{content}</div>
      )}
    </Link>
  );
};

// --- Main Sidebar Component ---

const Sidebar = ({ isDrawerOpen, setIsDrawerOpen }) => {
  const { theme } = useTheme();
  // const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Default expanded width is 280px, collapsed is 80px
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();

  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [isMasterOpen, setIsMasterOpen] = useState(false);
  const [isIntegrationsOpen, setIsIntegrationsOpen] = useState(false); // ✨ ADD THIS LINE
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const isMobile = useMediaQuery("(max-width:768px)");
  const isSubscribed = subscriptionStatus();
  const [userData, setUserData] = useState(null);
  const drawerRef = useRef(null);
  const bottomNavRef = useRef(null);
  const [logo, setLogo] = useState("");
  const [token] = useState(localStorage.getItem("token"));
  const [modules, setModules] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const location = useLocation();
  // --- Static Modules Definition (Moved for clarity) ---
  const getInitialStaticModules = (currentRole) => [
    {
      id: "Dashboard",
      name: "Dashboard",
      slug: "Home",
      path: "/dashboard/Home",
      icon: <MdDashboard />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "ProductionBatch",
      name: "Production Batch",
      slug: "ProductionBatch",
      path: "/dashboard/ProductionBatch",
      icon: <MdProductionQuantityLimits />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Suppliers",
      name: "Suppliers",
      slug: "Suppliers",
      path: "/dashboard/Suppliers",
      icon: <MdPeople />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Materials",
      name: "Materials",
      slug: "Materials",
      path: "/dashboard/Materials",
      icon: <MdCategory />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Products",
      name: "Products",
      slug: "Products",
      path: "/dashboard/Products",
      icon: <MdCategory />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Orders",
      name: "Orders",
      slug: "Orders",
      path: "/dashboard/Orders",
      icon: <MdShoppingCart />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Earnings",
      name: "Earnings",
      slug: "Earnings",
      path: "/dashboard/Earnings",
      icon: <MdAttachMoney />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Categories",
      name: "Categories",
      slug: "Categories",
      path: "/dashboard/Categories",
      icon: <MdCategory />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Expenses",
      name: "Expenses",
      slug: "Expenses",
      path: "/dashboard/Expenses",
      icon: <MdAccountBalance />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Customers",
      name: "Customers",
      slug: "Customers",
      path: "/dashboard/Customers",
      icon: <MdPeople />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "ShippingCompany",
      name: "Shipping Company",
      slug: "ShippingCompany",
      path: "/dashboard/ShippingCompany",
      icon: <MdPeople />,
      isHidden: false,
      isStatic: true,
    },
    {
      id: "Marketing",
      name: "Marketing",
      slug: "Marketing",
      path: "/dashboard/Marketing",
      icon: <MdCampaign />,
      isHidden: false,
      isStatic: true,
    },
    // {
    //   id: "Financial",
    //   name: "Financial Reports",
    //   slug: "Financial",
    //   path: "/dashboard/Financial",
    //   icon: <MdAccountBalance />,
    //   isHidden: false,
    //   isStatic: true,
    // },
    {
      id: "Financial",
      name: "Financial Reports",
      slug: "Financial",
      path: isSubscribed === "free" ? "#" : "/dashboard/Financial",
      icon: <MdAccountBalance />,
      isHidden: false,
      isStatic: true,
      isPremiumOnly: isSubscribed === "free",
      tooltip:
        isSubscribed === "free"
          ? "Upgrade to Premium to Access"
          : "Financial Reports",
    },
    {
      id: "PricingView",
      name: "Upgrade Plan",
      slug: "PricingView",
      path: "/dashboard/PricingView",
      icon: <MdAttachMoney />,
      isHidden: false,
      isStatic: true,
    },
    // {
    //   id: "Advertiser",
    //   name:
    //     currentRole !== "advertiser"
    //       ? "Become an Advertiser"
    //       : "Manage Your Ads",
    //   path:
    //     currentRole !== "advertiser"
    //       ? "/dashboard/BecomeAdvertiser"
    //       : "/dashboard/Advertiser",
    //   slug: currentRole !== "advertiser" ? "BecomeAdvertiser" : "Advertiser",
    //   icon: <GiSpades />,
    //   isHidden: false,
    //   isStatic: true,
    // },
  ];

  const [staticModuleState, setStaticModuleState] = useState(
    getInitialStaticModules(userData?.data.role)
  );

  // --- Combined Module Logic ---
  const allModules = [
    ...staticModuleState,
    ...modules.map((m) => ({
      ...m,
      icon: <MdCategory />,
      isStatic: false,
    })),
  ];

  const visibleModules = allModules.filter((m) => !m.isHidden);
  const hiddenModules = allModules.filter((m) => m.isHidden);

  // --- Module Action Handlers ---

  const fetchModules = async (token) => {
    try {
      const response = await axios.get(`${baseUrl}/api/module`, {
        headers: {
          Authorization: `Bearer ${token}`,
          timeout: 120000,
        },
      });
      if (Array.isArray(response)) {
        setModules(response.data);
      } else {
        setModules([]);
      }
    } catch (error) {
      console.error("Failed to fetch modules:", error);
    }
  };

  const handleEditModule = (moduleId) => {
    navigate(`/dashboard/ModuleEdit/${moduleId}`);
  };

  const handleDeleteModule = async (moduleId) => {
    const isStatic = staticModuleState.some((m) => m.id === moduleId);
    if (isStatic) {
      alert("You cannot delete built-in static modules.");
      return;
    }

    if (!window.confirm("This will permanently delete the module. Continue?")) {
      return;
    }

    // 👇 Instantly remove from UI
    setModules((prev) => prev.filter((m) => m.id !== moduleId));

    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${baseUrl}/api/module/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(`✅ Module ${moduleId} deleted successfully`);
    } catch (error) {
      console.error(`❌ Failed to delete module ${moduleId}:`, error);
      alert("Failed to delete module from the server. Please refresh.");
      // optional rollback:
      // fetchModules();
    }
  };

  const handleUpdateModuleHiddenStatus = async (moduleId, isHiddenStatus) => {
    const token = localStorage.getItem("token");
    const moduleToUpdate = allModules.find((m) => m.id === moduleId);
    const isStatic = moduleToUpdate?.isStatic;

    if (isStatic) {
      // Update local state for STATIC modules
      setStaticModuleState((prev) =>
        prev.map((m) =>
          m.id === moduleId ? { ...m, isHidden: isHiddenStatus } : m
        )
      );
      return;
    }

    // --- Dynamic Module Logic (API Call) ---

    // Optimistically update the local state for dynamic modules
    setModules(
      modules.map((m) =>
        m.id === moduleId ? { ...m, isHidden: isHiddenStatus } : m
      )
    );

    try {
      await axios.put(
        `${baseUrl}/api/module/${moduleId}`,
        { isHidden: isHiddenStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error(`Failed to update dynamic module ${moduleId}:`, error);
      alert(`Failed to update dynamic module. Please try again.`);

      // Revert the dynamic state if the API call fails
      setModules(
        modules.map((m) =>
          m.id === moduleId ? { ...m, isHidden: !isHiddenStatus } : m
        )
      );
    }
  };

  const handleHideModule = (moduleId) => {
    handleUpdateModuleHiddenStatus(moduleId, true);
  };

  const handleRestoreModule = (moduleId) => {
    handleUpdateModuleHiddenStatus(moduleId, false);
  };

  // --- Initial Data Fetch ---

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) return;

    const fetchUserData = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        console.log("dtaa", data);
        setLogo(data.logo);
        setUserData(data);
        // Update static module state with current role
        setStaticModuleState(getInitialStaticModules(data.role));
      } catch (error) {
        console.error("Error fetching user data:", error.message);
      }
    };

    fetchUserData();
    fetchModules(token);
  }, [token]);

  const toggleSettingsMenu = () => {
    setShowSettingsMenu(!showSettingsMenu);
    setIsMasterOpen(false);
    setIsAdminOpen(false);
  };
  useEffect(() => {
    // when route changes, re-fetch modules if we are in dashboard area
    if (token && location.pathname.startsWith("/dashboard")) {
      fetchModules(token);
    }
  }, [location.pathname]);
  // --- Settings Menu Data (Kept separate as it's a sub-menu) ---

  const settingsMenuItem = {
    name: "Settings",
    path: "#",
    icon: <MdSettings />,
    isSubMenu: true,
    subMenu: [
      { name: "Edit Profile", path: "/dashboard/EditProfile", icon: MdEdit },
      {
        name: "Change Password",
        path: "/dashboard/ChangePassword",
        icon: MdLock,
      },
      {
        name: "Integrations",
        path: "#",
        icon: <MdOutlineIntegrationInstructions />,
        isSubSubMenu: true,

        // 1. MATCH OPEN/TOGGLE SYNTAX
        isOpen: isIntegrationsOpen,
        toggle: () => setIsIntegrationsOpen(!isIntegrationsOpen),

        subSubMenu: [
          {
            name: "Google Form Webhook",
            path: "/dashboard/WebhookManager",
            // 2. MATCH ICON SYNTAX: Pass the component function (MdOutlineArticle), not the JSX element (<MdOutlineArticle />).
            icon: MdOutlineArticle,
          },
        ],
      },
      { name: "Logout", path: "/dashboard/Logout", icon: MdExitToApp },
      // {
      //   name: "Customize Theme",
      //   path: "/dashboard/CustomizeTheme",
      //   icon: MdPalette,
      // },
      {
        name: "Make a Complaint",
        path: "/dashboard/Complaint",
        icon: MdFeedback,
      },
      {
        name: "Customer Support",
        path: "/dashboard/CustomerSupport",
        icon: MdSupport,
      },
      {
        name: " Activity History",
        path: "/dashboard/AuditLogs",
        icon: TbActivity,
      },

      { name: "FAQs", path: "/dashboard/FAQs", icon: MdHelp },
      ...(userData?.data?.role === "master"
        ? [
            {
              name: "Master",
              path: "#",
              icon: MdHome,
              isSubSubMenu: true,
              isOpen: isMasterOpen,
              toggle: () => setIsMasterOpen(!isMasterOpen),
              subSubMenu: [
                {
                  name: "CreateFAQs",
                  path: "/dashboard/CreateFAQs",
                  icon: MdHelp,
                },
                { name: "ZoneManager", path: "/dashboard/Zones", icon: MdMap },
                {
                  name: "Contact us responses",
                  path: "/dashboard/ContactUsResponses",
                  icon: MdMailOutline,
                },
                {
                  name: "Companies",
                  path: "/dashboard/companies",
                  icon: FaBuilding,
                },
                {
                  name: "Main site Pages",
                  path: "/dashboard/Pages",
                  icon: MdPages,
                },
                {
                  name: "Master Dashboard",
                  path: "/dashboard/MasterDashboard",
                  icon: MdDashboard,
                },
                {
                  name: "Subscriptions",
                  path: "/dashboard/admin/subscriptions",
                  icon: MdSubscriptions, // or another icon
                },
                {
                  name: "Careers",
                  path: "/dashboard/Career",
                  icon: MdBusinessCenter,
                },
                {
                  name: "Pricing",
                  path: "/dashboard/Pricing",
                  icon: MdAttachMoney,
                },
              ],
            },
          ]
        : []),
      ...(userData?.data?.role === "master" || userData?.data?.role === "admin"
        ? [
            {
              name: "Admin",
              path: "#",
              icon: MdHome,
              isSubSubMenu: true,
              isOpen: isAdminOpen,
              toggle: () => setIsAdminOpen(!isAdminOpen),
              subSubMenu: [
                {
                  name: "Users Page",
                  path: "/dashboard/UsersPage",
                  icon: MdHome,
                },
                {
                  name: "Backup Manager",
                  path: "/dashboard/BackupManager",
                  icon: MdBackup,
                },
                {
                  name: "Reward Points Setting",
                  path: "/dashboard/RewardSetting",
                  icon: MdBusinessCenter,
                },
                {
                  name: "Shop Setting",
                  path: "/dashboard/ShopSetting",
                  icon: MdStorefront,
                },
              ],
            },
          ]
        : []),
      {
        name: "Trash",
        path: isSubscribed === "free" ? "#" : "/dashboard/Trash",
        icon: MdDelete,
        isPremiumOnly: isSubscribed === "free",
        tooltip:
          isSubscribed === "free" ? "Upgrade to Premium to Access" : "Trash",
      },
    ],
  };

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target) &&
        isDrawerOpen
      ) {
        closeDrawer();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDrawerOpen]);

  // --- Styles for Enterprise Look and Scroll Fix ---

  const sidebarWidth = isCollapsed ? "80px" : "280px"; // Wider sidebar
  const sidebarStyle = {
    width: sidebarWidth,
    backgroundColor: EnterpriseColors.SidebarBg,
    position: "fixed",
    top: "5rem",
    left: "0",
    height: "calc(100vh - 5rem)",
    color: EnterpriseColors.PrimaryText,
    boxShadow: "4px 0 12px rgba(0, 0, 0, 0.2)",
    transition: "width 0.3s ease",
    zIndex: "99",
    display: "flex",
    flexDirection: "column", // Use flex column for fixed header/footer and scrollable content
  };

  const menuSectionStyle = {
    // Scrollable area
    flexGrow: 1,
    overflowY: "auto",
    padding: isCollapsed ? "1rem 0" : "1rem 1.25rem",
    // Custom scrollbar style for better enterprise look
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: EnterpriseColors.HoverBg,
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  };
  const mobileBarContainerStyle = {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "60px",
    backgroundColor: EnterpriseColors.MobileBarBg,
    boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.2)",
    zIndex: 100,
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "0 5px",
  };

  const mobileLinkStyle = (isActive) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    color: isActive
      ? EnterpriseColors.MobileIconActive
      : EnterpriseColors.MobileIconInactive,
    fontSize: "24px",
    padding: "5px 0",
    flexGrow: 1,
  });
  const MobileItemText = ({ children, isActive }) => (
    <div
      style={{
        fontSize: "10px",
        marginTop: "2px",
        fontWeight: isActive ? 600 : 500,
      }}
    >
      {children}
    </div>
  );

  // Get the current path for active state highlighting
  const currentPath = window.location.pathname;
  const menuItemStyle = (index) => ({
    width: "100%",
    borderRadius: "6px",
    backgroundColor:
      hoveredIndex === index ? EnterpriseColors.HoverBg : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: isCollapsed ? "center" : "flex-start",
    padding: isCollapsed ? "12px 0" : "8px 12px",
    marginBottom: "4px",
    textDecoration: "none",
    transition: "background-color 0.15s ease",
  });

  const iconBaseStyle = (index, name) => ({
    fontSize: isCollapsed ? "24px" : "20px",
    color:
      hoveredIndex === index || hoveredIndex === name
        ? EnterpriseColors.IconHover
        : EnterpriseColors.IconDefault,
    transition: "color 0.15s ease",
    marginRight: isCollapsed ? "0" : "10px",
  });

  const textBaseStyle = {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    color: EnterpriseColors.PrimaryText,
    textAlign: "left",
  };

  // Separator style
  const separatorStyle = {
    borderTop: `1px solid ${EnterpriseColors.HoverBg}`,
    margin: "1rem 0",
  };

  return (
    <div>
      {isMobile ? (
        // Mobile Menu: Only render the off-screen Drawer

        <Drawer
          anchor="left"
          open={isDrawerOpen} // 💡 Must use the prop 'isDrawerOpen'
          onClose={closeDrawer}
          ModalProps={{ keepMounted: true }}
        >
          {/* Reuse the existing Drawer content */}

          <div
            ref={drawerRef}
            style={{
              width: 280,

              padding: "20px 20px 20px 20px", // Reduced bottom padding since bottom bar is gone

              backgroundColor: EnterpriseColors.SidebarBg,

              height: "100%",

              color: EnterpriseColors.PrimaryText,

              overflowY: "auto",
            }}
          >
            {/* --- 1. VISIBLE MODULES (Main Navigation) --- */}

            <h3
              style={{
                marginBottom: "15px",

                color: EnterpriseColors.SecondaryText,

                fontSize: "12px",

                fontWeight: 600,

                textTransform: "uppercase",
              }}
            >
              Primary Navigation
            </h3>

            {visibleModules.map((module) => (
              <ModuleMenuItem
                key={module.id}
                module={module}
                isCollapsed={false}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
                onEdit={handleEditModule}
                onDelete={handleDeleteModule}
                onHide={handleHideModule}
                onRestore={handleRestoreModule}
                closeDrawer={closeDrawer}
              />
            ))}

            <div style={separatorStyle}></div>

            {/* --- 2. SETTINGS MENU (Including Nested Items) --- */}

            <h3
              style={{
                marginBottom: "15px",

                color: EnterpriseColors.SecondaryText,

                fontSize: "12px",

                fontWeight: 600,

                textTransform: "uppercase",
              }}
            >
              Settings & Admin
            </h3>

            {settingsMenuItem.subMenu.map((subItem) => (
              <div key={subItem.name}>
                {subItem.isSubSubMenu ? (
                  <>
                    <div
                      onClick={subItem.toggle}
                      style={{
                        ...menuItemStyle(subItem.name),

                        justifyContent: "space-between",

                        cursor: "pointer",
                      }}
                      onMouseEnter={() => setHoveredIndex(subItem.name)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      {React.createElement(subItem.icon, {
                        style: iconBaseStyle(subItem.name, subItem.name),
                      })}

                      <span style={{ ...textBaseStyle, flexGrow: 1 }}>
                        {subItem.name}
                      </span>

                      {subItem.isOpen ? <MdChevronLeft /> : <MdChevronRight />}
                    </div>

                    {subItem.isOpen && (
                      <Box sx={{ paddingLeft: "15px" }}>
                        {subItem.subSubMenu.map((item) => (
                          <SubMenuItem
                            key={item.name}
                            name={item.name}
                            path={item.path}
                            icon={item.icon}
                            isCollapsed={false}
                            hoveredIndex={hoveredIndex}
                            setHoveredIndex={setHoveredIndex}
                          />
                        ))}
                      </Box>
                    )}
                    {/* Check the state to conditionally render the subSubMenu */}
                  </>
                ) : (
                  <SubMenuItem
                    key={subItem.name}
                    name={subItem.name}
                    path={subItem.path}
                    icon={subItem.icon}
                    isCollapsed={false}
                    hoveredIndex={hoveredIndex}
                    setHoveredIndex={setHoveredIndex}
                    isPremiumOnly={subItem.isPremiumOnly}
                    tooltip={subItem.tooltip}
                  />
                )}
              </div>
            ))}

            <div style={separatorStyle}></div>

            {/* --- 3. HIDDEN MODULES (Toggleable) --- */}

            {hiddenModules.length > 0 && (
              <div style={{ paddingBottom: "10px" }}>
                <div
                  onClick={() => setShowHidden(!showHidden)}
                  style={{
                    ...menuItemStyle("HiddenModules"),

                    cursor: "pointer",

                    backgroundColor: showHidden
                      ? EnterpriseColors.ActiveBg
                      : hoveredIndex === "HiddenModules"
                      ? EnterpriseColors.HoverBg
                      : "transparent",

                    height: "auto",

                    padding: "8px 12px",

                    justifyContent: "space-between",
                  }}
                  onMouseEnter={() => setHoveredIndex("HiddenModules")}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <span style={textBaseStyle}>
                    {showHidden
                      ? "Hide Deleted Modules"
                      : "Show Deleted Modules"}
                  </span>

                  {showHidden ? <MdVisibilityOff /> : <MdVisibility />}
                </div>

                {showHidden && (
                  <Box sx={{ marginTop: "10px" }}>
                    {hiddenModules.map((module) => (
                      <ModuleMenuItem
                        key={module.id}
                        module={module}
                        isCollapsed={false}
                        hoveredIndex={hoveredIndex}
                        setHoveredIndex={setHoveredIndex}
                        onEdit={handleEditModule}
                        onDelete={handleDeleteModule}
                        onHide={handleHideModule}
                        onRestore={handleRestoreModule}
                        closeDrawer={closeDrawer}
                      />
                    ))}
                  </Box>
                )}
              </div>
            )}
          </div>
        </Drawer>
      ) : (
        // Desktop Sidebar
        <div style={sidebarStyle}>
          {/* 1. Logo Section (Fixed Top) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isCollapsed ? "center" : "flex-start",
              height: 70,
              padding: isCollapsed ? "0 10px" : "0 20px",
              borderBottom: `1px solid ${EnterpriseColors.HoverBg}`,
              flexShrink: 0, // Prevent shrinking when content scrolls
            }}
          >
            <img
              src={userData?.data?.logo}
              alt="Company Logo"
              style={{
                maxHeight: "50px",
                maxWidth: isCollapsed ? "40px" : "180px",
                objectFit: "contain",
                margin: "10px 0",
                // filter: "brightness(0) invert(1)", // Optional: Invert logo color for dark background
              }}
            />

            {/* Change: Add a text fallback when logo is missing */}
            {!logo && (
              <span
                style={{
                  ...textBaseStyle,
                  fontSize: "18px",
                  fontWeight: 600,
                  marginLeft: "10px",
                }}
              >
                ERP
              </span>
            )}

            {/* Change: Ensure Company Name is shown when not collapsed */}
            {!isCollapsed && userData && (
              <span
                style={{
                  ...textBaseStyle,
                  fontSize: "18px",
                  fontWeight: 600,
                  marginLeft: "10px",
                }}
              >
                {userData?.data?.companyName}
              </span>
            )}
          </Box>

          {/* 2. Scrollable Content Area (Modules) */}
          <Box sx={menuSectionStyle}>
            {/* Create Module Button */}
            {!isCollapsed && (
              <Link
                to="/dashboard/moduleCreate"
                style={{
                  display: "block",
                  margin: "0 0 1rem 0",
                  textDecoration: "none",
                }}
              >
                <button
                  style={{
                    width: "100%",
                    padding: "10px 0",
                    borderRadius: "6px",
                    // Change 1: Use light grey background
                    backgroundColor: EnterpriseColors.NewModuleButtonBg,
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // Change 2: Use dark text
                    color: EnterpriseColors.NewModuleButtonText,
                    fontSize: "16px",
                    fontWeight: 600,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseEnter={(e) =>
                    // Change 3: Use a hover color
                    (e.currentTarget.style.backgroundColor =
                      EnterpriseColors.NewModuleButtonHover)
                  }
                  onMouseLeave={(e) =>
                    // Change 4: Revert to base color
                    (e.currentTarget.style.backgroundColor =
                      EnterpriseColors.NewModuleButtonBg)
                  }
                >
                  <MdAddCircleOutline
                    style={{ fontSize: "20px", marginRight: "8px" }}
                  />
                  Create New Module
                </button>
              </Link>
            )}
            {isCollapsed && (
              <Tooltip title="Create New Module" placement="right" arrow>
                <Link
                  to="/dashboard/moduleCreate"
                  style={{
                    display: "block",
                    margin: "0 auto 1rem auto",
                    textDecoration: "none",
                    width: "56px",
                    height: "56px",
                    borderRadius: "6px",
                    // Change 1: Use light grey background
                    backgroundColor: EnterpriseColors.NewModuleButtonBg,
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // Change 2: Use dark text for icon color
                    color: EnterpriseColors.NewModuleButtonText,
                    fontSize: "24px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseEnter={(e) =>
                    // Change 3: Use a hover color
                    (e.currentTarget.style.backgroundColor =
                      EnterpriseColors.NewModuleButtonHover)
                  }
                  onMouseLeave={(e) =>
                    // Change 4: Revert to base color
                    (e.currentTarget.style.backgroundColor =
                      EnterpriseColors.NewModuleButtonBg)
                  }
                >
                  <MdAddCircleOutline />
                </Link>
              </Tooltip>
            )}

            <div style={separatorStyle}></div>

            {/* Visible Modules List */}
            {!isCollapsed && (
              <div
                style={{
                  ...textBaseStyle,
                  color: EnterpriseColors.SecondaryText,
                  marginBottom: "8px",
                }}
              >
                Primary Navigation
              </div>
            )}
            {visibleModules.map((module) => (
              <ModuleMenuItem
                key={module.id}
                module={module}
                isCollapsed={isCollapsed}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
                onEdit={handleEditModule}
                onDelete={handleDeleteModule}
                onHide={handleHideModule}
                onRestore={handleRestoreModule}
                closeDrawer={closeDrawer}
              />
            ))}

            {/* Hidden Modules Section */}
            {hiddenModules.length > 0 && (
              <div style={{ paddingBottom: "10px" }}>
                <div style={separatorStyle}></div>
                <div
                  onClick={() => setShowHidden(!showHidden)}
                  style={{
                    ...menuItemStyle("HiddenModules"),
                    cursor: "pointer",
                    backgroundColor: showHidden
                      ? EnterpriseColors.ActiveBg
                      : hoveredIndex === "HiddenModules"
                      ? EnterpriseColors.HoverBg
                      : "transparent",
                    height: "auto",
                    padding: isCollapsed ? "12px 0" : "8px 12px",
                    justifyContent: isCollapsed ? "center" : "space-between",
                  }}
                  onMouseEnter={() => setHoveredIndex("HiddenModules")}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isCollapsed ? "center" : "flex-start",
                      width: isCollapsed ? "auto" : "100%",
                    }}
                  >
                    <MdVisibilityOff
                      style={iconBaseStyle("HiddenModules", "HiddenModules")}
                    />
                    {!isCollapsed && (
                      <span style={{ ...textBaseStyle, marginLeft: "10px" }}>
                        Hidden Modules ({hiddenModules.length})
                      </span>
                    )}
                  </div>
                  {!isCollapsed && (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {showHidden ? (
                        <MdChevronLeft color={EnterpriseColors.PrimaryText} />
                      ) : (
                        <MdChevronRight color={EnterpriseColors.PrimaryText} />
                      )}
                    </div>
                  )}
                </div>
                {showHidden && (
                  <div style={{ paddingTop: "4px" }}>
                    {hiddenModules.map((module) => (
                      <ModuleMenuItem
                        key={module.id}
                        module={module}
                        isCollapsed={isCollapsed}
                        hoveredIndex={hoveredIndex}
                        setHoveredIndex={setHoveredIndex}
                        onEdit={handleEditModule}
                        onDelete={handleDeleteModule}
                        onRestore={handleRestoreModule}
                        onHide={handleHideModule}
                        closeDrawer={closeDrawer}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* 3. Settings Menu (Fixed Bottom) */}
            <Box
              sx={{
                flexShrink: 0,
                padding: isCollapsed ? "1rem 0.5rem" : "1rem 1.25rem",
                borderTop: `1px solid ${EnterpriseColors.HoverBg}`,
              }}
            >
              <Link
                to={
                  settingsMenuItem.path === "#"
                    ? window.location.pathname
                    : settingsMenuItem.path
                }
                onClick={toggleSettingsMenu}
                style={{
                  ...menuItemStyle("setting-link"),
                  padding: isCollapsed ? "12px 0" : "8px 12px",
                  justifyContent: isCollapsed ? "center" : "space-between",
                }}
                onMouseEnter={() => setHoveredIndex("setting-link")}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={iconBaseStyle("setting-link", settingsMenuItem.name)}
                  >
                    {settingsMenuItem.icon}
                  </div>
                  {!isCollapsed && (
                    <span style={{ ...textBaseStyle, marginLeft: "10px" }}>
                      {settingsMenuItem.name}
                    </span>
                  )}
                </div>
                {/* Collapse/Expand Indicator for Settings */}
                {!isCollapsed && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {showSettingsMenu ? (
                      <MdChevronLeft color={EnterpriseColors.PrimaryText} />
                    ) : (
                      <MdChevronRight color={EnterpriseColors.PrimaryText} />
                    )}
                  </div>
                )}
              </Link>

              {/* Settings Submenu */}
              {showSettingsMenu && !isCollapsed && (
                <div style={{ paddingTop: "8px" }}>
                  {settingsMenuItem.subMenu.map((subItem) => {
                    if (!subItem.isSubSubMenu) {
                      return (
                        <SubMenuItem
                          key={subItem.name}
                          name={subItem.name}
                          path={subItem.path}
                          icon={subItem.icon}
                          isCollapsed={isCollapsed}
                          hoveredIndex={hoveredIndex}
                          setHoveredIndex={setHoveredIndex}
                          isPremiumOnly={subItem.isPremiumOnly}
                          tooltip={subItem.tooltip}
                        />
                      );
                    }
                    // Sub-Sub Menus (Master/Admin)
                    return (
                      <div key={subItem.name}>
                        <div
                          onClick={subItem.toggle}
                          style={{
                            ...menuItemStyle(subItem.name),
                            backgroundColor: subItem.isOpen
                              ? EnterpriseColors.ActiveBg
                              : hoveredIndex === subItem.name
                              ? EnterpriseColors.HoverBg
                              : "transparent",
                            cursor: "pointer",
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            marginLeft: "12px",
                            width: "calc(100% - 12px)",
                          }}
                          onMouseEnter={() => setHoveredIndex(subItem.name)}
                          onMouseLeave={() => setHoveredIndex(null)}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div
                              style={iconBaseStyle(subItem.name, subItem.name)}
                            >
                              {subItem.icon}
                            </div>
                            <span
                              style={{ ...textBaseStyle, marginLeft: "10px" }}
                            >
                              {subItem.name}
                            </span>
                          </div>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {subItem.isOpen ? (
                              <MdChevronLeft
                                color={EnterpriseColors.PrimaryText}
                              />
                            ) : (
                              <MdChevronRight
                                color={EnterpriseColors.PrimaryText}
                              />
                            )}
                          </div>
                        </div>
                        {subItem.isOpen && (
                          <div style={{ paddingTop: "4px" }}>
                            {subItem.subSubMenu.map((subSubItem) => (
                              <SubMenuItem
                                key={subSubItem.name}
                                name={subSubItem.name}
                                path={subSubItem.path}
                                icon={subSubItem.icon}
                                isCollapsed={isCollapsed}
                                hoveredIndex={hoveredIndex}
                                setHoveredIndex={setHoveredIndex}
                                isPremiumOnly={false}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Box>

            {/* Toggle Button (Fixed Position) */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              style={{
                background: EnterpriseColors.SidebarBg,
                border: `1px solid ${EnterpriseColors.HoverBg}`,
                borderRadius: "50%",
                cursor: "pointer",
                position: "absolute",
                top: "65px", // Adjusted to be near the header/content border
                right: isCollapsed ? "-12px" : "-12px",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                transform: "translateY(-50%)",
              }}
            >
              {isCollapsed ? (
                <MdChevronRight
                  size={18}
                  color={EnterpriseColors.IconDefault}
                />
              ) : (
                <MdChevronLeft size={18} color={EnterpriseColors.IconDefault} />
              )}
            </button>
          </Box>

          {/* 3. BOTTOM SECTION (The Footer) */}
          {/* 3. BOTTOM SECTION (The Footer) */}
          {/* 3. Footer (Fixed Bottom) */}
          <div
            style={{
              padding: "15px",
              borderTop: `1px solid ${EnterpriseColors.HoverBg}`,
              textAlign: "center",
              fontSize: "12px",
              color: EnterpriseColors.SecondaryText,
              backgroundColor: EnterpriseColors.SidebarBg,
              flexShrink: 0, // Prevents footer from being squeezed
            }}
          >
            {!isCollapsed ? (
              <span>&copy; Copyrights by Spade {new Date().getFullYear()}</span>
            ) : (
              <span>&copy; {new Date().getFullYear()}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
